// src/pages/CardBattle.js - Following BumFit Game Rules
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import CardDisplay from '../components/CardDisplay';
import {
    getAllCards,
    getUnlockedCards,
    getCardsByType,
    CARD_TYPES
} from '../data/BattleCardsData';
import { soundManager } from '../utils/SoundManager';
import { STATIC_CARD_ORDER, getTotalCardCount } from '../data/StaticCardMapping';
import { getCardContent } from '../data/CardContent';
import './CardBattle.css';

const CardBattle = () => {
    const navigate = useNavigate();
    const { battleId } = useParams();

    // Game State
    const [gamePhase, setGamePhase] = useState('setup'); // setup, facedown, reveal, reaction, result
    const [round, setRound] = useState(1);
    const [currentPlayer, setCurrentPlayer] = useState('player');

    // Player Stats
    const [playerHealth, setPlayerHealth] = useState(20);
    const [playerStoredReps, setPlayerStoredReps] = useState(0);
    const [playerRepDebt, setPlayerRepDebt] = useState(0);
    const [playerHand, setPlayerHand] = useState([]);
    const [playerFaceDownCards, setPlayerFaceDownCards] = useState([]);

    // Opponent Stats
    const [opponentHealth, setOpponentHealth] = useState(20);
    const [opponentStoredReps, setOpponentStoredReps] = useState(0);
    const [opponentRepDebt, setOpponentRepDebt] = useState(0);
    const [opponentHand, setOpponentHand] = useState([]);
    const [opponentFaceDownCards, setOpponentFaceDownCards] = useState([]);

    // Battle State
    const [availableCards, setAvailableCards] = useState([]);
    const [selectedCards, setSelectedCards] = useState([]);
    const [revealedCards, setRevealedCards] = useState([]);
    const [battleLog, setBattleLog] = useState([]);
    const [allowedCardTypes, setAllowedCardTypes] = useState([]);
    const [playerScore, setPlayerScore] = useState(0);
    const [opponentScore, setOpponentScore] = useState(0);

    useEffect(() => {
        console.log('CardBattle component mounted, initializing game...');
        initializeGame();
    }, []);

    const createVirtualCardForBattle = (mappedCard, position) => {
        const content = getCardContent(position.toString().padStart(3, '0'));
        return {
            id: mappedCard.id,
            cardNumber: position,
            name: content ? content.characterName : `Card ${position}`,
            type: mappedCard.folder === 'battle-cards' ? 'Battle' :
                mappedCard.folder === 'build-cards' ? 'Build' :
                    mappedCard.folder === 'train-cards' ? 'Train' : 'Move',
            image: `/assets/cards/${mappedCard.folder}/${mappedCard.image}`,
            rarity: { name: 'Common', color: '#808080' },
            unlocked: true,
            attack: Math.floor(Math.random() * 10) + 5,
            defense: Math.floor(Math.random() * 8) + 3,
            health: Math.floor(Math.random() * 15) + 10,
            topLeft: Math.floor(Math.random() * 8) + 2,
            topRight: Math.floor(Math.random() * 5) + 1,
            bottomLeft: Math.floor(Math.random() * 12) + 3,
            bottomRight: Math.floor(Math.random() * 6) + 2,
            triangles: Math.floor(Math.random() * 7) + 1
        };
    };

    const initializeGame = () => {
        console.log('🎮 Initializing game...');

        try {
            // Create all 520 virtual cards
            const allVirtualCards = STATIC_CARD_ORDER.map(mappedCard =>
                createVirtualCardForBattle(mappedCard, mappedCard.position)
            );

            console.log('📦 All cards loaded:', allVirtualCards.length);

            setAvailableCards(allVirtualCards);
            const shuffled = [...allVirtualCards].sort(() => 0.5 - Math.random());
            setPlayerHand(shuffled.slice(0, 5));
            setOpponentHand(shuffled.slice(5, 10));
            console.log('👤 Player hand set with all cards:', shuffled.slice(0, 5).length, 'cards');

            setGamePhase('facedown');
            updateAllowedCardTypes('facedown');
            soundManager.playSound('gameIntroMode2');
            addToBattleLog("Game started! Place 1-3 cards face-down.");
        } catch (error) {
            console.error('❌ Error initializing game:', error);
        }
    };
    const updateAllowedCardTypes = (phase) => {
        console.log('🔄 Updating allowed card types for phase:', phase);

        switch (phase) {
            case 'facedown':
                // Can place Train, Build, or Battle cards face-down
                setAllowedCardTypes([CARD_TYPES.TRAINER, CARD_TYPES.BUILD, CARD_TYPES.BATTLE]);
                console.log('✅ Allowed types set to:', [CARD_TYPES.TRAINER, CARD_TYPES.BUILD, CARD_TYPES.BATTLE]);
                break;
            case 'instant':
                // Can play Train or Move cards instantly
                setAllowedCardTypes([CARD_TYPES.TRAINER, CARD_TYPES.MOVE]);
                console.log('✅ Allowed types set to:', [CARD_TYPES.TRAINER, CARD_TYPES.MOVE]);
                break;
            case 'reaction':
                // Can play Battle cards to react, or Train/Move for reps
                setAllowedCardTypes([CARD_TYPES.BATTLE, CARD_TYPES.TRAINER, CARD_TYPES.MOVE]);
                console.log('✅ Allowed types set to:', [CARD_TYPES.BATTLE, CARD_TYPES.TRAINER, CARD_TYPES.MOVE]);
                break;
            default:
                setAllowedCardTypes([]);
                console.log('⚠️ No allowed types set for phase:', phase);
        }
    };

    const getCardTypeInstruction = () => {
        if (allowedCardTypes.length === 0) return "Choose your card";

        if (gamePhase === 'facedown') {
            return "Choose your Battle/Build/Train cards (1-3 face-down)";
        } else if (gamePhase === 'instant') {
            return "Choose your Train/Move card (instant play)";
        } else if (gamePhase === 'reaction') {
            return "Choose your Battle card (reaction) or Train/Move card (reps)";
        }

        const typeNames = allowedCardTypes.join('/');
        return `Choose your ${typeNames} card`;
    };

    const addToBattleLog = (message) => {
        setBattleLog(prev => [...prev, {
            id: `${Date.now()}-${Math.random()}`, // Make keys truly unique
            message,
            timestamp: new Date(),
            round: round,
            player: currentPlayer
        }]);
    };

    const canPlayCard = (card) => {
        return allowedCardTypes.includes(card.type);
    };

    const handleCardSelect = (card) => {
        if (!canPlayCard(card)) {
            addToBattleLog(`Cannot play ${card.type} cards in current phase.`);
            return;
        }

        if (gamePhase === 'facedown') {
            handleFaceDownCardSelect(card);
        } else if (gamePhase === 'instant') {
            handleInstantCardPlay(card);
        } else if (gamePhase === 'reaction') {
            handleReactionCardPlay(card);
        }

        soundManager.playSound('next');
    };

    const handleFaceDownCardSelect = (card) => {
        if (selectedCards.length >= 3) {
            addToBattleLog("Maximum 3 face-down cards allowed.");
            return;
        }

        if (selectedCards.find(c => c.id === card.id)) {
            // Deselect card
            setSelectedCards(prev => prev.filter(c => c.id !== card.id));
        } else {
            // Select card
            setSelectedCards(prev => [...prev, card]);
        }
    };

    const handleInstantCardPlay = (card) => {
        if (card.type === CARD_TYPES.TRAINER) {
            playTrainCard(card);
        } else if (card.type === CARD_TYPES.MOVE) {
            playMoveCard(card);
        }
    };

    const playTrainCard = (card) => {
        // Train card gives reps based on workout sequence
        const baseReps = 3; // Simplified - normally based on workout sequence length
        const repsEarned = baseReps * round; // Round multiplier

        setPlayerStoredReps(prev => prev + repsEarned);
        addToBattleLog(`Played Train card! Earned ${repsEarned} reps (${baseReps} × Round ${round})`);

        // Remove from hand
        setPlayerHand(prev => prev.filter(c => c.id !== card.id));
    };

    const playMoveCard = (card) => {
        // Move card - player chooses how many reps to do (1 to triangles)
        const maxReps = card.triangles || 1;
        const chosenReps = parseInt(window.prompt(`How many reps will you do? (1-${maxReps})`)) || 1;
        const actualReps = Math.min(Math.max(1, chosenReps), maxReps);
        const repsEarned = actualReps * round; // Round multiplier

        setPlayerStoredReps(prev => prev + repsEarned);
        addToBattleLog(`Played Move card! Did ${actualReps} reps, earned ${repsEarned} stored reps`);

        // Remove from hand
        setPlayerHand(prev => prev.filter(c => c.id !== card.id));
    };

    const handleReactionCardPlay = (card) => {
        if (card.type === CARD_TYPES.BATTLE) {
            // Battle reaction - attack back or defend
            const choice = window.confirm("Attack back? (OK = Attack, Cancel = Defend)");
            if (choice) {
                attemptAttack(card);
            } else {
                attemptDefend(card);
            }
        } else {
            // Train/Move card for emergency reps
            handleInstantCardPlay(card);
        }
    };

    const attemptAttack = (battleCard) => {
        const attackCost = getBattleCardAttackCost(battleCard);
        const attackDamage = getBattleCardAttackDamage(battleCard);

        if (playerStoredReps >= attackCost) {
            setPlayerStoredReps(prev => prev - attackCost);
            addToBattleLog(`Attacked for ${attackDamage} damage! (Cost: ${attackCost} reps)`);

            // Deal damage to opponent
            dealDamageToOpponent(attackDamage);
        } else {
            const deficit = attackCost - playerStoredReps;
            const useDebt = window.confirm(`Not enough reps! Add ${deficit} to your debt (TAB)?`);

            if (useDebt) {
                setPlayerRepDebt(prev => prev + deficit);
                setPlayerStoredReps(0);
                addToBattleLog(`Attacked for ${attackDamage} damage! Added ${deficit} to debt.`);
                dealDamageToOpponent(attackDamage);
            } else {
                addToBattleLog("Attack cancelled - insufficient reps.");
            }
        }
    };

    const attemptDefend = (battleCard) => {
        const defendCost = getBattleCardDefendCost(battleCard);
        const defendValue = getBattleCardDefendValue(battleCard);

        if (playerStoredReps >= defendCost) {
            setPlayerStoredReps(prev => prev - defendCost);
            addToBattleLog(`Defended for ${defendValue} damage reduction! (Cost: ${defendCost} reps)`);
        } else {
            const deficit = defendCost - playerStoredReps;
            setPlayerRepDebt(prev => prev + deficit);
            setPlayerStoredReps(0);
            addToBattleLog(`Defended for ${defendValue} damage reduction! Added ${deficit} to debt.`);
        }
    };

    const getBattleCardAttackCost = (card) => {
        if (round >= 3) {
            // Round 3+: Can use any corner number
            return Math.min(card.topLeft, card.topRight, card.bottomLeft, card.bottomRight);
        }
        // Normally would be specific to card design, simplified here
        return card.bottomRight || 5;
    };

    const getBattleCardAttackDamage = (card) => {
        return card.bottomLeft || card.attack || 5;
    };

    const getBattleCardDefendCost = (card) => {
        if (round >= 3) {
            // Round 3+: Can use any corner number
            return Math.min(card.topLeft, card.topRight, card.bottomLeft, card.bottomRight);
        }
        return card.topRight || 2;
    };

    const getBattleCardDefendValue = (card) => {
        return card.topLeft || card.defense || 3;
    };

    const dealDamageToOpponent = (damage) => {
        // In real game, this would target specific cards first
        // For now, let's directly reduce opponent health
        setOpponentHealth(prev => {
            const newHealth = Math.max(0, prev - damage); // Actual damage instead of just -1
            console.log(`💥 Opponent takes ${damage} damage! Health: ${prev} → ${newHealth}`);
            if (newHealth === 0) {
                endGame('victory');
            }
            return newHealth;
        });
    };

    const dealDamageToPlayer = (damage) => {
        setPlayerHealth(prev => {
            const newHealth = Math.max(0, prev - damage);
            console.log(`💥 You take ${damage} damage! Health: ${prev} → ${newHealth}`);
            if (newHealth === 0) {
                endGame('defeat');
            }
            return newHealth;
        });
    };
    const confirmFaceDownCards = () => {
        if (selectedCards.length === 0) {
            addToBattleLog("Select at least 1 card to place face-down.");
            return;
        }

        setPlayerFaceDownCards(selectedCards);
        setSelectedCards([]);
        setGamePhase('reveal');
        updateAllowedCardTypes('reaction');

        addToBattleLog(`Placed ${selectedCards.length} cards face-down. Opponent's turn to reveal.`);
        soundManager.playSound('attack');

        // Auto-opponent reveal (simplified)
        setTimeout(simulateOpponentReveal, 2000);
    };

    const simulateOpponentReveal = () => {
        if (playerFaceDownCards.length === 0) {
            console.log('⚠️ No face-down cards to reveal');
            // Switch to opponent's turn if no cards to reveal
            simulateOpponentTurn();
            return;
        }

        const numToReveal = Math.min(playerFaceDownCards.length, Math.floor(Math.random() * 3) + 1);
        const revealed = playerFaceDownCards.slice(0, numToReveal);

        console.log('🎭 Opponent revealing cards:', revealed.length);
        console.log('🃏 Revealed cards:', revealed.map(c => c.name));

        setRevealedCards(revealed);
        setGamePhase('reaction');
        setCurrentPlayer('player'); // Player's turn to react

        addToBattleLog(`Opponent revealed ${numToReveal} cards! Choose your reaction.`);
        soundManager.playSound('defend');
    };

    const simulateOpponentTurn = () => {
        console.log('🤖 Opponent taking turn...');
        setCurrentPlayer('opponent');

        // Give opponent some random face-down cards
        const opponentCards = availableCards.slice(10, 13); // Get 3 random cards
        setOpponentFaceDownCards(opponentCards);

        addToBattleLog(`Opponent placed ${opponentCards.length} cards face-down.`);

        // After a delay, let player choose to reveal
        setTimeout(() => {
            setGamePhase('reveal');
            setCurrentPlayer('player');
            addToBattleLog("Your turn! Choose how many opponent cards to reveal.");
        }, 1500);
    };

    const handlePlayerReveal = (numCards) => {
        if (opponentFaceDownCards.length === 0) {
            console.log('⚠️ No opponent cards to reveal');
            return;
        }

        const revealed = opponentFaceDownCards.slice(0, numCards);
        setRevealedCards(revealed);
        setGamePhase('reaction');

        console.log('👤 Player revealing opponent cards:', revealed.length);
        addToBattleLog(`You revealed ${revealed.length} opponent cards! Choose your reaction.`);
        soundManager.playSound('attack');
    };

    const endGame = (result) => {
        setGamePhase('result');

        if (result === 'victory') {
            addToBattleLog("VICTORY! You defeated your opponent!");
            soundManager.playSound('workoutComplete2');
        } else {
            addToBattleLog("DEFEAT! Train harder and try again!");
            soundManager.playSound('villain');
        }
    };

    const resetGame = () => {
        setGamePhase('setup');
        setRound(1);
        setPlayerHealth(20);
        setOpponentHealth(20);
        setPlayerStoredReps(0);
        setPlayerRepDebt(0);
        setOpponentStoredReps(0);
        setOpponentRepDebt(0);
        setSelectedCards([]);
        setRevealedCards([]);
        setPlayerFaceDownCards([]);
        setOpponentFaceDownCards([]);
        setBattleLog([]);
        initializeGame();
    };
    return (
        <div className="card-battle">
            {/* Header */}
            <div className="battle-header">
                <button className="back-button" onClick={() => navigate('/card-gallery')}>
                    ← Back to Gallery
                </button>
                <h1>Card Battle Arena - Round {round}</h1>
                <div className="battle-controls">
                    <button className="reset-button" onClick={resetGame}>
                        New Game
                    </button>
                </div>
            </div>

            {/* Disclaimer */}
            <div style={{
                background: 'rgba(255, 255, 255, 0.1)',
                padding: '15px',
                borderRadius: '10px',
                margin: '20px 0',
                textAlign: 'center',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: '#ffaa00'
            }}>
                <strong>📋 Digital simplified version</strong> - see <a href="https://bumfit.com/rules.html" target="_blank" rel="noopener noreferrer" style={{color: '#00ccff', textDecoration: 'none'}}>full rules</a> for complete graveyard and rep management systems
            </div>

            {/* Game Stats */}
            <div className="game-stats">
                <div className="player-stats">
                    <h3>Your Stats</h3>
                    <div>Health: {playerHealth}/20</div>
                    <div>Stored Reps: {playerStoredReps}</div>
                    <div>Rep Debt: {playerRepDebt}</div>
                    <div>Hand: {playerHand.length} cards</div>
                </div>
                <div className="game-info">
                    <div className="phase-indicator">
                        Phase: {gamePhase.toUpperCase()}
                    </div>
                    <div className="round-info">
                        Round {round} (×{round} rep multiplier)
                    </div>
                </div>
                <div className="opponent-stats">
                    <h3>Opponent Stats</h3>
                    <div>Health: {opponentHealth}/20</div>
                    <div>Stored Reps: {opponentStoredReps}</div>
                    <div>Rep Debt: {opponentRepDebt}</div>
                    <div>Hand: {opponentHand.length} cards</div>
                </div>
            </div>

            {/* Battle Area */}
            <div className="battle-area">
                {/* Face-down cards display */}
                {playerFaceDownCards.length > 0 && (
                    <div className="face-down-area">
                        <h3>Your Face-Down Cards</h3>
                        <div className="face-down-cards">
                            {playerFaceDownCards.map((card, index) => (
                                <div key={card.id} className="face-down-card">
                                    {revealedCards.includes(card) ? (
                                        <CardDisplay
                                            card={card}
                                            size="medium"
                                            showStats={true}
                                            interactive={false}
                                        />
                                    ) : (
                                        <div className="card-back">Face Down</div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Show opponent's face-down cards */}
                {opponentFaceDownCards.length > 0 && (
                    <div className="face-down-area">
                        <h3>Opponent's Face-Down Cards</h3>
                        <div className="face-down-cards">
                            {opponentFaceDownCards.map((card, index) => (
                                <div key={`opponent-${card.id}-${index}`} className="face-down-card">
                                    {revealedCards.includes(card) ? (
                                        <CardDisplay
                                            card={card}
                                            size="medium"
                                            showStats={true}
                                            interactive={false}
                                        />
                                    ) : (
                                        <div className="card-back">Face Down</div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Revealed cards that need reactions */}
                {revealedCards.length > 0 && gamePhase === 'reaction' && (
                    <div className="reaction-area">
                        <h3>Revealed Cards - Choose Your Reaction</h3>
                        <div className="revealed-cards">
                            {revealedCards.map(card => (
                                <div key={card.id} className="revealed-card">
                                    <CardDisplay
                                        card={card}
                                        size="medium"
                                        showStats={true}
                                        interactive={false}
                                    />
                                    <div className="card-stats">
                                        {card.type === CARD_TYPES.BATTLE && (
                                            <>
                                                <div>Attack: {getBattleCardAttackDamage(card)} (Cost: {getBattleCardAttackCost(card)})</div>
                                                <div>Defend: {getBattleCardDefendValue(card)} (Cost: {getBattleCardDefendCost(card)})</div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Player Reveal Choice */}
            {gamePhase === 'reveal' && currentPlayer === 'player' && opponentFaceDownCards.length > 0 && (
                <div className="card-selection">
                    <h3>🎯 Opponent's Turn Complete - Choose How Many Cards to Reveal</h3>

                    <div className="action-section">
                        <button
                            className="action-button primary"
                            onClick={() => handlePlayerReveal(1)}
                        >
                            Reveal 1 Card
                        </button>
                        <button
                            className="action-button primary"
                            onClick={() => handlePlayerReveal(Math.min(2, opponentFaceDownCards.length))}
                        >
                            Reveal 2 Cards
                        </button>
                        <button
                            className="action-button primary"
                            onClick={() => handlePlayerReveal(Math.min(3, opponentFaceDownCards.length))}
                        >
                            Reveal 3 Cards
                        </button>
                    </div>

                    <div style={{ color: '#999', fontSize: '0.9rem', margin: '15px 0' }}>
                        Opponent has {opponentFaceDownCards.length} face-down cards
                    </div>
                </div>
            )}

            {/* Card Selection */}
            {(gamePhase === 'facedown' || gamePhase === 'instant' || gamePhase === 'reaction') && (
                <div className="card-selection">
                    <h3>{getCardTypeInstruction()}</h3>

                    {/* Debug info */}
                    <div style={{ color: '#999', fontSize: '0.8rem', margin: '10px 0', background: 'rgba(0,0,0,0.3)', padding: '10px', borderRadius: '5px' }}>
                        <div>🎯 Phase: {gamePhase}</div>
                        <div>👤 Player Hand: {playerHand.length} cards</div>
                        <div>📦 Available Cards: {availableCards.length} cards</div>
                        <div>🎭 Allowed Types: {allowedCardTypes.join(', ')}</div>
                        <div>🔍 Filtered Cards: {playerHand.filter(card => allowedCardTypes.length === 0 || canPlayCard(card)).length}</div>
                        {playerHand.length > 0 && (
                            <div>🃏 Sample Card: {playerHand[0]?.name} ({playerHand[0]?.type})</div>
                        )}
                    </div>

                    {/* Action buttons */}
                    {gamePhase === 'facedown' && selectedCards.length > 0 && (
                        <div className="action-section">
                            <button className="action-button primary" onClick={confirmFaceDownCards}>
                                Place {selectedCards.length} Cards Face-Down
                            </button>
                        </div>
                    )}

                    {/* Quick Battle Test Button */}
                    {gamePhase === 'facedown' && (
                        <div className="action-section">
                            <button
                                className="action-button secondary"
                                onClick={() => {
                                    console.log('🚀 Quick battle test');
                                    setGamePhase('result');
                                    addToBattleLog('Quick battle test completed!');
                                }}
                                style={{ marginLeft: '10px' }}
                            >
                                🚀 Quick Battle Test
                            </button>
                        </div>
                    )}

                    {/* Reaction Phase Buttons */}
                    {gamePhase === 'reaction' && (
                        <div className="action-section">
                            <div style={{ color: '#999', fontSize: '0.8rem', margin: '10px 0' }}>
                                🎭 Reaction Phase: {revealedCards.length} cards revealed
                            </div>
                            <button
                                className="action-button primary"
                                onClick={() => {
                                    console.log('⚔️ Auto-attack reaction');
                                    const playerDamage = Math.floor(Math.random() * 5) + 3; // 3-7 damage
                                    const opponentDamage = Math.floor(Math.random() * 5) + 3; // 3-7 damage

                                    setPlayerScore(playerDamage);
                                    setOpponentScore(opponentDamage);

                                    // Deal actual damage to health
                                    if (playerDamage > opponentDamage) {
                                        dealDamageToOpponent(playerDamage - opponentDamage);
                                        addToBattleLog(`You win this round! Dealt ${playerDamage - opponentDamage} damage to opponent.`);
                                    } else if (opponentDamage > playerDamage) {
                                        dealDamageToPlayer(opponentDamage - playerDamage);
                                        addToBattleLog(`Opponent wins this round! You take ${opponentDamage - playerDamage} damage.`);
                                    } else {
                                        addToBattleLog(`Draw this round! No damage dealt.`);
                                    }

                                    setGamePhase('result');
                                    addToBattleLog('Battle completed! Check results.');
                                }}
                            >
                                ⚔️ Attack Back
                            </button>
                            <button
                                className="action-button secondary"
                                onClick={() => {
                                    console.log('🛡️ Defend reaction');
                                    addToBattleLog('You defended against the attack!');

                                    // Clear current battle and start opponent's turn
                                    setRevealedCards([]);
                                    setPlayerFaceDownCards([]);

                                    // Start opponent's turn
                                    simulateOpponentTurn();
                                }}
                            >
                                🛡️ Defend & Continue
                            </button>
                            <button
                                className="action-button secondary"
                                onClick={() => {
                                    console.log('⏭️ Skip to results');
                                    setGamePhase('result');
                                    addToBattleLog('Round completed!');
                                }}
                                style={{ marginLeft: '10px' }}
                            >
                                ⏭️ Skip to Results
                            </button>
                        </div>
                    )}

                    <div className="available-cards">
                        {playerHand.length === 0 ? (
                            <div style={{ color: '#999', textAlign: 'center', padding: '20px' }}>
                                No cards in hand. Check console for errors.
                            </div>
                        ) : (
                            playerHand.filter(card => allowedCardTypes.length === 0 || canPlayCard(card)).map(card => (
                                <CardDisplay
                                    key={card.id}
                                    card={card}
                                    size="medium"
                                    showStats={true}
                                    interactive={true}
                                    onClick={handleCardSelect}
                                    isSelected={selectedCards.find(c => c.id === card.id)}
                                    isOwned={true}
                                    disabled={!canPlayCard(card)}
                                />
                            ))
                        )}
                    </div>
                </div>
            )}

            {/* Battle Log */}
            <div className="battle-log">
                <h3>Battle Log</h3>
                <div className="log-entries">
                    {battleLog.slice(-10).map(entry => (
                        <div key={entry.id} className={`log-entry ${entry.player}`}>
                            <span className="round-info">R{entry.round}</span>
                            <span className="message">{entry.message}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Game Result */}
            {gamePhase === 'result' && (
                <div className="game-result">
                    <div className="result-content">
                        <h2>Round Complete!</h2>
                        <div className="final-stats">
                            <div>Your Health: {playerHealth}/20</div>
                            <div>Opponent Health: {opponentHealth}/20</div>
                            <div>Your Attack: {playerScore}</div>
                            <div>Opponent Attack: {opponentScore}</div>
                            <div>Round: {round}</div>
                        </div>

                        {/* Check for game over */}
                        {playerHealth === 0 || opponentHealth === 0 ? (
                            <>
                                <h3 style={{ color: playerHealth === 0 ? '#ff3333' : '#00ff88' }}>
                                    {playerHealth === 0 ? '💀 GAME OVER - YOU LOSE!' : '🏆 VICTORY - YOU WIN!'}
                                </h3>
                                <button className="action-button primary" onClick={resetGame}>
                                    Play Again
                                </button>
                                <button className="action-button secondary" onClick={() => navigate('/card-gallery')}>
                                    Back to Gallery
                                </button>
                            </>
                        ) : (
                            <>
                                <h3>Continue Battle</h3>
                                <button
                                    className="action-button primary"
                                    onClick={() => {
                                        setGamePhase('facedown');
                                        setRevealedCards([]);
                                        setPlayerFaceDownCards([]);
                                        setOpponentFaceDownCards([]);
                                        setPlayerScore(0);
                                        setOpponentScore(0);
                                        setRound(prev => prev + 1);
                                        updateAllowedCardTypes('facedown');
                                        addToBattleLog(`=== ROUND ${round + 1} STARTS ===`);
                                    }}
                                >
                                    Next Round
                                </button>
                                <button className="action-button secondary" onClick={() => navigate('/card-gallery')}>
                                    End Game
                                </button>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default CardBattle;